/****************************************************
 * Module Name:  commission.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React from "react";
import { useEffect } from "react";
import Constants from "../../utility/constants";
import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import CommissionProxy from "../../utility/proxy/manager/commissionProxy";
import { MultiSelect } from "../common/reactSelectWithSelectAll";
import { RefreshOutlined, SearchOutlined } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import Utility from "../../utility/utility";
import { BsFilePdf } from "react-icons/bs";
import CommonSnackbar from "../common/commonSnackBar";
import UserProxy from "../../utility/proxy/manager/userProxy";
import NoPermission from "../common/noPermission";
import PageTitle from "../common/pageTitle";


const Commission = () => {
  const [data, setData] = useState([]);
  const [broker, setBroker] = useState([]);
  const [brokerOptions, setBrokerOptions] = useState([]);
  const [fetchData, setFetchData] = useState(false);
  const [listLoader, setListLoader] = useState(false);
  const [errorSnackbar, seterrorSnackbar] = useState(false);
  const [filterValues, setFilterValues] = useState({});

  useEffect(() => {
    PageTitle("REPORT_COMMISSION_COMMISSION");
    checkPermission();
  }, []);

  const checkPermission = () => {
    let allowed = Constants.USER_PERMISSION.find(
      (iPermission) =>
        iPermission.permissionid ===
        Constants.PERMISSIONS.REPORT_COMMISSION_COMMISSION
    ) !== undefined
    if (allowed) {
      fetchBrokers();
    } else if (!allowed && Constants.loggedInUser.usertype === "B") {
      handleSearch();
    } else {
      return <NoPermission />
    }
  }

  useEffect(() => {
    if (fetchData) {
      if (broker) {
        handleSearch();
      }
    }
  }, [fetchData, broker]);

  const fetchBrokers = () => {
    UserProxy.GetAllBrokerMini((response) => {
      if (response)
        processBrokerData(response);
    });
  };

  const fullOptionToValue = (selectedData, mainData) => {
    let newArr = [];
    selectedData.map((data) => {
      if (data.value === "*") {
        return (mainData.map((option) => {
          if (option.value !== "*") {
            newArr.push(option.value);
            return true;
          }
          return true;
        }));
      }
      return true;
    });
    return Utility.GetValueArrayWithStarReplace(selectedData, newArr);
  };

  const processBrokerData = (response) => {
    let optionsArray = [];
    optionsArray.push({ value: "*", label: "-- ANY --" });
    response.map((data) => {
      let option = {
        value: data.id,
        label: data.companyname,
      };
      optionsArray.push(option);
      return true;
    });
    setBrokerOptions(optionsArray);
    setBroker([optionsArray[0]]);
    setFetchData(true);
  };

  const handleSearch = () => {
    let data =
      Constants.loggedInUser.usertype === "B"
        ? [Constants.loggedInUser.id]
        : fullOptionToValue(broker, brokerOptions);
    if (data.length > 0) {
      setListLoader(true);
      CommissionProxy.GetAllCommission(data, (responseData) => {
        if (responseData) {
          const uniqueFilterValues = {
            type: Utility.getDistinctOptions(responseData, "type"),
            status: Utility.getDistinctOptions(responseData, "status"),
          };
          setFilterValues(uniqueFilterValues);
          setData(responseData);
          setListLoader(false);
          setFetchData(false);
        } else {
          seterrorSnackbar(true)
          setListLoader(false);
          setFetchData(false);
        }
      });
    } else {
      //warning
    }
  };

  const initialColumns = () => [
    {
      field: "id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 0.5,
    },
    {
      field: "userid",
      hheaderName: "USER ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 0.5,
    },
    {
      field: "brokercompanyname",
      headerName: "BROKER",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 0.5,
    },
    {
      field: "date",
      headerName: "DATE",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    {
      field: "updatedat",
      headerName: "UPDATED AT",
      headerAlign: "center",
      align: "center",
      minWidth: 150,
      flex: 1,
      type: "dateTime",
      valueGetter: (params) => {
        if (params.value) {
          return new Date(params.value);
        }
        return null;
      },
      renderCell: (params) => Utility.ConvertUtcToLocalTime(params.value),
    },
    {
      field: "invoiceid",
      headerName: "INVOICE ID",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 0.5,
    },
    {
      field: "type",
      headerName: "TYPE",
      headerAlign: "center",
      align: "center",
      minWidth: 120,
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.type : [],
      filterOperators: Utility.combinedFilters(),
      flex: 1,
    },
    {
      field: "amount",
      headerName: "AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      flex: 1,
      type: "number",
      valueFormatter: (params) => params.value,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "paidamount",
      headerName: "PAID AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 1,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "invoiceamount",
      headerName: "INVOICE AMOUNT",
      headerAlign: "center",
      align: "center",
      type: "number",
      valueFormatter: (params) => params.value,
      minWidth: 110,
      flex: 1,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "finalamount",
      headerName: "FINAL AMOUNT",
      headerAlign: "center",
      align: "center",
      minWidth: 110,
      type: "number",
      valueFormatter: (params) => params.value,
      flex: 1,
      renderCell: (params) => Utility.ConvertNumberWithSpecifiedDigits(params.value)
    },
    {
      field: "status",
      headerName: "STATUS",
      headerAlign: "center",
      align: "center",
      type: "singleSelect",
      valueOptions: filterValues ? filterValues.status : [],
      filterOperators: Utility.combinedFilters(),
      width: 100,
    },
    {
      field: "pdf",
      headerName: "DOWNLOAD PDF",
      headerAlign: "center",
      align: "center",
      minWidth: 50,
      flex: 0.5,
      renderCell: (params) => (
        <a
          href={`data:application/pdf;base64,${params.value}`}
          download="Commission.pdf"
          style={{ color: "red" }}
        >
          <BsFilePdf size={25} />
        </a>
      ),
    },
  ];

  const rows = data;
  const columns = initialColumns();

  return (
    <Box>
      <CommonSnackbar
        show={errorSnackbar}
        message={Constants.APiFailErrorMessage}
        variant={"error"}
        autoHide={Constants.SnackBarCloseTimer}
        title={"Failed"}
        handleClose={() => seterrorSnackbar(false)}
      />

      <Box
        sx={{
          display: "flex",
          width: "100%",
          my: 1,
          gap: "15px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            width: Constants.loggedInUser.usertype !== "B" ? "25%" : "30%",
            gap: "20px",
            pl: 1,
          }}
        >
          <Box sx={{ alignSelf: "center", mt: 2, width: "185px" }}>
            <Typography>Broker Company-</Typography>
          </Box>
          {Constants.loggedInUser.usertype !== "B" ? (
            <Box
              sx={{
                alignSelf: "center",
                width: "80%",
                mt: 2,
              }}
            >
              <MultiSelect
                options={brokerOptions}
                value={broker}
                onChange={(selectedOption) => {
                  setBroker(selectedOption);
                }}
              />
            </Box>
          ) : (
            <Typography mt={2} fontWeight={600}>
              {Constants.loggedInUser.username}
            </Typography>
          )}
        </Box>
        <Box sx={{ marginTop: "17px", ml: 2 }}>
          <LoadingButton
            variant="contained"
            onClick={() => handleSearch()}
            startIcon={<SearchOutlined size="large" />}
            loading={listLoader}
            loadingPosition="start"
            disabled={
              brokerOptions.length === 0 &&
              Constants.loggedInUser.usertype !== "B"
            }
          >
            Search
          </LoadingButton>
        </Box>
      </Box>
      <Box
        sx={{
          backgroundColor: '#fff',
          color: '#000',
          width: "100%",
          position: "relative",
          "& .MuiTablePagination-selectLabel": {
            marginTop: "15px",
          },
          "& .MuiTablePagination-displayedRows": {
            marginTop: "15px",
          },
          "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
            fontWeight: Constants.defaultFontWeight,
          },
          "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
            color: "#1976d2",
            padding: "4px 5px",
            borderRadius: "4px",
          },
          "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
            color: "#1976d2",
            padding: "5px 5px",
            borderRadius: "4px",
          },
          "& .css-cc8tf1": {
            fontWeight: Constants.defaultFontWeight,
          },
          height: "80vh",
        }}
      >
        <DataGridPro
          disableRowSelectionOnClick={true}
          slots={{ toolbar: GridToolbar }}
          rows={rows}
          sx={{
            '& .MuiDataGrid-columnHeader': {
              color: '#fff',
              backgroundColor: 'grey',
            },
            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
              py: "4px",
            },
            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
              py: "12px",
            },
            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
              py: "18px",
            },

            fontSize: Constants.defaultTableFontSize, fontWeight: 'bold'
          }}
          density="compact"
          columns={columns}
          getRowId={(row) => row.id}
          loading={listLoader}
          initialState={{
            columns: {
              columnVisibilityModel: {
                // Hide columns id, the other columns will remain visible
                locationid: false,
                userid: false,
              },
            },
            pagination: {
              paginationModel: Constants.pageSize,
            },
          }}
          pageSizeOptions={Constants.pageSizeOptions}
          pagination
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
              },
            },
          }}
        />
        <Box
          style={{
            position: "absolute",
            top: 5,
            right: "270px",
          }}
        >
          {listLoader ? (
            <CircularProgress size="1rem" />
          ) : (
            <Tooltip title="Reload List">
              <RefreshOutlined
                onClick={() => handleSearch()}
                color="primary"
                fontSize="medium"
                sx={{ cursor: "pointer", mr: 1 }}
              />
            </Tooltip>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Commission;
